/**
 * Timezone utilities for Central Africa Time (CAT)
 * CAT is UTC+2 - Juba, South Sudan (no daylight saving time)
 * 
 * Note: MongoDB stores all datetime objects in UTC. The timestamps retrieved
 * from the database are in UTC and need to be converted to CAT for display.
 * 
 * All times are displayed in 24-hour format.
 */

/**
 * Format a date/timestamp to CAT timezone (Juba, South Sudan - UTC+2)
 * Assumes input is in UTC (as stored by MongoDB)
 * @param {string|Date} timestamp - The timestamp to format (assumed UTC)
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in CAT
 */
export const formatCAT = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';
  
  let date;
  if (typeof timestamp === 'string') {
    // Parse the timestamp
    date = new Date(timestamp);
    // If no timezone in string, assume it's UTC from MongoDB
    if (!timestamp.includes('+') && !timestamp.includes('Z') && !timestamp.includes('-', 10)) {
      // Append Z to indicate UTC
      date = new Date(timestamp + 'Z');
    }
  } else {
    date = timestamp;
  }
  
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  // Format in CAT timezone (UTC+2) - using Africa/Khartoum which is same timezone
  const defaultOptions = {
    timeZone: 'Africa/Khartoum', // UTC+2 same as Juba, South Sudan
    hour12: false, // Use 24-hour format
    ...options
  };
  
  return date.toLocaleString('en-GB', defaultOptions); // en-GB uses 24-hour by default
};

/**
 * Format date only in CAT
 * @param {string|Date} timestamp 
 * @returns {string}
 */
export const formatCATDate = (timestamp) => {
  return formatCAT(timestamp, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Format date and time in CAT (24-hour format)
 * @param {string|Date} timestamp 
 * @returns {string}
 */
export const formatCATDateTime = (timestamp) => {
  return formatCAT(timestamp, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format time only in CAT (24-hour format)
 * @param {string|Date} timestamp 
 * @returns {string}
 */
export const formatCATTime = (timestamp) => {
  return formatCAT(timestamp, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};
